import React, { Dispatch, Reducer, ReducerAction, ReducerState, useReducer } from 'react';
import { ALERT, AlertField, IClusterDevicesListParams } from '../../../services/core/maps';
import { getTimePeriodParams } from '@lib/dateParams';
import { Filter } from '@components/timePeriodAndFilterPicker/useFilterPicker';

type ClusterState = Pick<IClusterDevicesListParams, 'alertField' | 'alertValues' | 'from' | 'to' | 'groups' | 'homeLocations' | 'zoneId'> & { selectedCluster: string[] };

type ClusterAction =
  | { type: 'SET_SELECTED_CLUSTER', payload: string[] }
  | { type: 'SET_ALERT_FIELD', payload: AlertField }
  | { type: 'TOGGLE_ALERT_VALUE', payload: ALERT }
  | { type: 'SET_TIME_PERIOD', payload: number }
  | { type: 'SET_FILTER', payload: Filter };

type ClusterReducer = Reducer<ClusterState, ClusterAction>;

export const ClusterContext = React.createContext<{
  state: ReducerState<ClusterReducer>,
  dispatch: Dispatch<ReducerAction<ClusterReducer>>
}>({
  state: undefined,
  dispatch: undefined
});

export const useClusterContext = () => {
  return useReducer<Reducer<ClusterState, ClusterAction>>(
    (state, action) => {
      switch (action.type) {
      case 'SET_SELECTED_CLUSTER': {
        return { ...state, selectedCluster: action.payload };
      }

      case 'SET_ALERT_FIELD': {
        let selectedCluster = null;
        let alertField = action.payload;
        let alertValues = [...(state?.alertValues ?? [])];

        if (!alertField) {
          alertValues = undefined;
        } else if (!alertValues.length) {
          alertValues = [ALERT.yellow, ALERT.red];
        }

        if (alertField === 'deviceTimeErrorStatus') {
          alertValues = [ALERT.red];
        }

        return { ...state, selectedCluster, alertField, alertValues };
      }

      case 'TOGGLE_ALERT_VALUE': {
        let alertField = state.alertField;
        let alertValues = [...(state?.alertValues ?? [])];

        if (alertValues.includes(action.payload)) {
          alertValues.splice(alertValues.indexOf(action.payload), 1);
        } else {
          alertValues.push(action.payload);
        }

        if (!alertValues.length) {
          alertValues = undefined;
          alertField = undefined;
        }

        return { ...state, alertField, selectedCluster: null, alertValues };
      }

      case 'SET_TIME_PERIOD': {
        const timePeriodParams = getTimePeriodParams(action.payload);
        return { ...state, from: timePeriodParams?.from, to: timePeriodParams?.to };
      }

      case 'SET_FILTER': {
        return {
          ...state,
          groups: !action.payload.group ? undefined : [action.payload.group.value],
          homeLocations: !action.payload.homeLocation ? undefined : [action.payload.homeLocation.value.id],
          zoneId: action.payload.zone?.value.id
        };
      }

      default: {
        return state;
      }
      }
    },
    {
      from: undefined,
      to: undefined,
      groups: undefined,
      homeLocations: undefined,
      zoneId: undefined,
      alertField: undefined,
      alertValues: undefined,
      selectedCluster: undefined
    }
  );
};
